<template>
  <div v-if="showModal" class="modal" @click="handleBackdropClick">
    <div class="popup-content">
      <!-- Header -->
      <div class="popup-header">
        <h3>User Details</h3>
        <button @click="closeModal" class="close-btn">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="popup-body">
        <!-- Loading Spinner -->
        <div v-if="loading" class="loading-spinner">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>

        <!-- User Details Boxes -->
        <div v-else class="user-details-container">
          <!-- Full Name -->
          <div v-if="userDetails.first_name" class="user-detail-box">
            <strong>Full Name:</strong>
            <span>{{ (userDetails.title ? userDetails.title + '.' : '') + ' ' + (userDetails.first_name || '') + ' ' + (userDetails.last_name || '') }}</span>
          </div>

          <!-- Status -->
          <div v-if="userDetails.isActive" class="user-detail-box">
            <strong>Status:</strong>
            <span
                class="status-dot"
                :class="userDetails.isActive === '1' ? 'active' : 'inactive'">
          </span>
            <span class="status-text">
            {{ userDetails.isActive === '1' ? 'Active' : 'Inactive' }}
          </span>
          </div>

          <!-- Company Name -->
          <div v-if="userDetails.company_id" class="user-detail-box">
            <strong>Company Name:</strong>
            <span>{{ userDetails.company_id || 'N/A' }}</span>
          </div>

          <!-- Business Name -->
          <div v-if="userDetails.businessName" class="user-detail-box">
            <strong>Business Name:</strong>
            <span>{{ userDetails.businessName || 'N/A' }}</span>
          </div>

          <!-- User Role -->
          <div v-if="userDetails.role" class="user-detail-box">
            <strong>User Role:</strong>
            <span>{{ userDetails.role || 'N/A' }}</span>
          </div>

          <!-- Reporting To -->
          <div v-if="userDetails.reporting_name" class="user-detail-box">
            <strong>Reporting To:</strong>
            <span>{{ userDetails.reporting_name || 'N/A' }}</span>
          </div>

          <!-- Email -->
          <div v-if="userDetails.email" class="user-detail-box">
            <strong>Email:</strong>
            <span>{{ userDetails.email || 'N/A' }}</span>
          </div>

          <!-- Mobile Number -->
          <div v-if="userDetails.mobile" class="user-detail-box">
            <strong>Mobile Number:</strong>
            <span>{{ userDetails.mobile || 'N/A' }}</span>
          </div>

          <!-- Address 1 -->
          <div v-if="userDetails.address1" class="user-detail-box">
            <strong>Address 1:</strong>
            <span>{{ userDetails.address1 || 'N/A' }}</span>
          </div>

          <!-- Address 2 -->
          <div v-if="userDetails.address2" class="user-detail-box">
            <strong>Address 2:</strong>
            <span>{{ userDetails.address2 || 'N/A' }}</span>
          </div>

          <!-- City -->
          <div v-if="userDetails.city" class="user-detail-box">
            <strong>City:</strong>
            <span>{{ userDetails.city || 'N/A' }}</span>
          </div>

          <!-- District -->
          <div v-if="userDetails.district_name" class="user-detail-box">
            <strong>District:</strong>
            <span>{{ userDetails.district_name || 'N/A' }}</span>
          </div>

          <!-- SVAT Number -->
          <div v-if="userDetails.svat_no" class="user-detail-box">
            <strong>SVAT Number:</strong>
            <span>{{ userDetails.svat_no || 'N/A' }}</span>
          </div>

          <!-- VAT Number -->
          <div v-if="userDetails.vat_no" class="user-detail-box">
            <strong>VAT Number:</strong>
            <span>{{ userDetails.vat_no || 'N/A' }}</span>
          </div>

          <!-- Remark -->
          <div v-if="userDetails.remark" class="user-detail-box">
            <strong>Remark:</strong>
            <span>{{ userDetails.remark || 'N/A' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { ViewUserDetailsById } from "@/services/dataService";

export default {
  name: 'ViewUserModal',
  props: {
    showModal: Boolean,
    userId: [String, Number],
    token: { type: String, required: true }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const loading = ref(false);
    const userDetails = ref({});

    const closeModal = () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      emit('close');
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && props.showModal) closeModal();
    };

    const handleBackdropClick = (event) => {
      if (event.target.classList.contains('modal')) closeModal();
    };

    const fetchUserDetails = async (id) => {
      if (!id) return;
      try {
        loading.value = true;
        const response = await ViewUserDetailsById(id, props.token);
        if (response.data?.data) {
          const data = response.data.data;
          userDetails.value = {
            isActive: data.is_active,
            company_id: data.company_id == 1 ? "LAUGFS Gas PLC" : "LAUGFS Lubricants Ltd",
            title: data.title,
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            mobile: data.mobile,
            address1: data.address1,
            address2: data.address2,
            city: data.city,
            businessName: data.business_name,
            district_name: data.district_name,
            svat_no: data.svat_no,
            vat_no: data.vat_no,
            reporting_name: data.reporting_name,
            remark: data.remark,
            role: data.name
          };
        }
      } catch (err) {
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    watch([() => props.userId, () => props.showModal], ([id, show]) => {
      if (show && id) fetchUserDetails(id);
    });

    onMounted(() => document.addEventListener('keydown', handleEscape));
    onUnmounted(() => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    });

    return { loading, userDetails, closeModal, handleBackdropClick };
  }
};
</script>

<style scoped>
/* Modal Overlay */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Modal Content */
.popup-content {
  background-color: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

/* Fixed Header */
.popup-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fff; /* keeps it solid when scrolling */
  position: sticky;
  top: 0;
  z-index: 10;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
.popup-header h3 {
  margin: 0;
  color: #000;
  font-weight: bold;
  font-size: 18px;
}

/* Close Button */
.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s, color 0.2s;
}
.close-btn:hover {
  color: #B22222;
}

/* Scrollable Body */
.popup-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* Scrollbar style */
.popup-body::-webkit-scrollbar {
  width: 8px;
}

/* Scrollbar Thumb styling */
.popup-body::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);
  border-radius: 4px;
}

/* Scrollbar Thumb Hover styling */
.popup-body::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0,0,0,0.4); /* darker on hover */
}

/* Loading Spinner */
.loading-spinner {
  text-align: center;
  padding: 40px 20px;
  font-size: 16px;
  color: #007bff;
}

/* User Detail Boxes */
.user-details-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.user-detail-box {
  flex: 0 0 calc(50% - 12px); /* always take half row */
  max-width: calc(50% - 12px); /* prevent stretching */
  background-color: #f6f6f6;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.user-detail-box strong {
  display: block;
  margin-bottom: 4px;
  color: #333;
}
.user-detail-box span {
  color: #555;
}

/* Status Dot */
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

/* Active / Inactive Colors */
.status-dot.active {
  background-color: #28a745; /* Green */
}

.status-dot.inactive {
  background-color: #dc3545; /* Red */
}

/* Status Text */
.status-text {
  vertical-align: middle;
  color: #555;
}
</style>